import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { IoAdd, IoImageOutline, IoClose } from "react-icons/io5";
import CategorySelect from "../../components/CategorySelect";
import { MainCategory } from "../../data/categories";
import GearButton from "../../ui/gearbutton";
import { addGearItem } from "../../backend/api/gearlpal";
import GearInput from "../../ui/gearinput";

import { convertToWebP } from "../../utils/imageUtils";

interface AddGearItemFormProps {
  gearpalId: number;
  onSuccess?: () => void;
}

export default function AddGearItemForm({
  gearpalId,
  onSuccess,
}: AddGearItemFormProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isConverting, setIsConverting] = useState(false);

  const [newItem, setNewItem] = useState<{
    name: string;
    categoryId: string;
    categoryLabel: string;
    externalLink: string;
    description: string;
    noleggio: number;
  }>({
    name: "",
    categoryId: "",
    categoryLabel: "",
    externalLink: "",
    description: "",
    noleggio: 0,
  });

  const { mutate: addItemMutation, isPending: isAdding } = useMutation({
    mutationFn: ({ id, data }: { id: number; data: FormData }) =>
      addGearItem(id, data, i18n.language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gearpals"] });
      queryClient.invalidateQueries({ queryKey: ["my-gearpals"] });
      toast.success(t("forms.itemAdded", "Articolo aggiunto con successo!"));
      if (onSuccess) onSuccess();
      // Reset form
      setNewItem({
        name: "",
        categoryId: "",
        categoryLabel: "",
        externalLink: "",
        description: "",
        noleggio: 0,
      });
      setSelectedFiles([]);
    },
    onError: (error: any) => {
      console.error("Errore aggiunta item:", error);
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        t("errors.unknownError");
      toast.error(`${t("errors.error")}: ${msg}`);
    },
  });

  const handleAddItem = async () => {
    if (!newItem.name || !newItem.categoryId || !gearpalId) return;

    try {
      setIsConverting(true);
      const formData = new FormData();
      formData.append("name", newItem.name);
      formData.append("categoryId", newItem.categoryId);
      formData.append("externalLink", newItem.externalLink);
      formData.append("description", newItem.description);
      formData.append("noleggio", String(newItem.noleggio));

      if (selectedFiles.length > 0) {
        // Convert all files to WebP
        const convertedFiles = await Promise.all(
          selectedFiles.map((file) => convertToWebP(file))
        );

        convertedFiles.forEach((file) => {
          // Important: Key must be exactly "images[]" repeated for each file.
          // It is a file field, not an array field, but we send multiples with same key.
          formData.append("images[]", file);
        });
      }

      addItemMutation({ id: gearpalId, data: formData });
    } catch (err) {
      console.error(err);
      toast.error(t("errors.unknownError"));
    } finally {
      setIsConverting(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles((prev) => [
        ...prev,
        ...Array.from(e.target.files || []),
      ]);
    }
  };

  const removeImage = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  if (!gearpalId) return null;

  return (
    <div className="space-y-4 w-full">
      {/* Category Selection */}

      {/* Name Input */}
      <GearInput
        type="text"
        label={t("forms.itemNameLabel", "Nome articolo")}
        placeholder={t("forms.itemName", "Nome articolo")}
        value={newItem.name}
        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
      />

      {/* External Link Input */}
      <div className="flex items-center gap-3">
        <GearInput
          type="url"
          className="w-full"
          containerClassName="w-full"
          label={t("forms.externalLinkLabel", "Link esterno")}
          placeholder={t("forms.externalLink", "Link esterno (opzionale)")}
          value={newItem.externalLink}
          onChange={(e) =>
            setNewItem({ ...newItem, externalLink: e.target.value })
          }
        />
        {/* Noleggio Checkbox */}
        <GearInput
          type="number"
          id="noleggio"
          label={t("forms.rentalCost", "Costo noleggio")}
          value={newItem.noleggio}
          onChange={(e) =>
            setNewItem({ ...newItem, noleggio: parseInt(e.target.value) })
          }
        />
      </div>

      <div style={{ marginTop: "0px" }}>
        <CategorySelect
          value={
            newItem.categoryLabel
              ? {
                  main: newItem.categoryLabel.split(" > ")[0] as MainCategory,
                  sub: newItem.categoryLabel.split(" > ")[1],
                  type: newItem.categoryLabel.split(" > ")[2],
                }
              : null
          }
          onChange={(val) => {
            if (val) {
              setNewItem((prev) => ({
                ...prev,
                categoryId: val.type,
                categoryLabel: `${val.main} > ${val.sub} > ${val.type}`,
              }));
            }
          }}
          useTranslationKeys={true}
          useBackendCategories={true}
        />
      </div>

      <GearInput
        type="textarea"
        minRows={3}
        label={t("forms.descriptionLabel", "Descrizione")}
        placeholder={t("forms.description", "Descrizione (opzionale)")}
        value={newItem.description}
        onChange={(e) =>
          setNewItem({ ...newItem, description: e.target.value })
        }
      />

      {/* Image Upload */}
      <div className="flex flex-col gap-3">
        <label
          style={{
            border: "1px solid #451671",
          }}
          className="w-full flex flex-col items-center bg-[#331855] justify-center h-[200px] max-w-[200px] border-white/20 rounded-xl cursor-pointer hover:border-primary/50 hover:bg-white/5 transition-all"
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <img src="/icons/upload.svg" alt="" />
            <p className="text-sm mt-4 text-secondary">
              {t("forms.clickToUpload", "Clicca per caricare immagini")}
            </p>
          </div>
          <GearInput
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={handleImageChange}
          />
        </label>

        {selectedFiles.length > 0 && (
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 mt-2">
            {selectedFiles.map((img, idx) => (
              <div
                key={idx}
                className="relative group aspect-square rounded-lg overflow-hidden border border-white/10"
              >
                <img
                  src={URL.createObjectURL(img)}
                  alt={`preview ${idx}`}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => removeImage(idx)}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <IoClose size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-2">
        <GearButton
          variant="secondary"
          onClick={handleAddItem}
          disabled={
            !newItem.name || !newItem.categoryId || isAdding || isConverting
          }
          className="w-full sm:w-auto !rounded-full"
        >
          <div className="flex items-center gap-2 justify-center">
            <IoAdd size={20} />
            {isAdding || isConverting
              ? t("common.saving", "Salvataggio...")
              : t("forms.addItemButton", "Aggiungi al GearPal")}
          </div>
        </GearButton>
      </div>
    </div>
  );
}
